import { useState, useEffect } from 'react';
import AxiosInstance from 'src/components/Axios';

// Модульні кеші — живуть протягом сесії браузера.
// Скидаються вручну через invalidateSupportCategories() або при перезавантаженні сторінки.
let cacheActive = null;
let cacheInactive = null;

/**
 * Скидає обидва кеші категорій підтримки (активні та неактивні).
 * Викликати після успішного створення або редагування категорії у SupportCategoriesForm,
 * щоб наступні виклики хуків зробили свіжі запити на сервер.
 */
export const invalidateSupportCategories = () => {
	cacheActive = null;
	cacheInactive = null;
};

/**
 * Хук для отримання списку активних категорій підтримки (`/support-categories?is_active=true`).
 *
 * Перший виклик робить HTTP-запит і зберігає результат у модульному кеші.
 * Усі наступні виклики (навіть у різних компонентах) повертають кешовані дані
 * без повторного запиту. Для примусового оновлення — викликати invalidateSupportCategories()
 * перед наступним рендером компонента, що використовує цей хук.
 *
 * Кожна категорія містить поле `scopes` — масив кодів областей застосування
 * (напр. `['knowledge_base', 'all']`), що дозволяє фільтрувати категорії на стороні клієнта.
 *
 * @returns {Array} Масив об'єктів активних категорій. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const categories = useSupportCategories();
 * // [{ id: 1, name: 'Авторизація', slug: 'auth', scopes: ['knowledge_base'], is_active: true }, ...]
 *
 * // Фільтрація для Q&A (scope: knowledge_base або all):
 * const qaOptions = categories.filter(c => c.scopes?.some(s => s === 'knowledge_base' || s === 'all'));
 */
export const useSupportCategories = () => {
	const [items, setItems] = useState(cacheActive || []);

	useEffect(() => {
		if (cacheActive) return;
		AxiosInstance.get('/support-categories', { params: { is_active: true, itemsPerPage: 200 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheActive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};

/**
 * Хук для отримання списку неактивних категорій підтримки (`/support-categories?is_active=false`).
 *
 * Поведінка кешування аналогічна useSupportCategories — один запит на сесію.
 * Кеш спільно скидається через invalidateSupportCategories().
 *
 * Використовується для відображення застарілих (відключених) категорій,
 * що вже прив'язані до Q&A / FAQ / листів.
 *
 * @returns {Array} Масив об'єктів неактивних категорій. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const inactiveCategories = useInactiveSupportCategories();
 * // [{ id: 5, name: 'Старий розділ', slug: 'old', scopes: ['knowledge_base'], is_active: false }, ...]
 */
export const useInactiveSupportCategories = () => {
	const [items, setItems] = useState(cacheInactive || []);

	useEffect(() => {
		if (cacheInactive) return;
		AxiosInstance.get('/support-categories', { params: { is_active: false, itemsPerPage: 200 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheInactive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};
